/**
 * OAuth client and token manager.
 *
 * Owns the browser side of the gateway's confidential-client OAuth flow so the
 * session module stays a pure storage seam and the api module stays a pure fetch
 * wrapper. The flow mirrors the native app: the app kicks off sign-in at
 * /v1/auth/oauth/init, the browser is redirected to the identity provider, the
 * gateway bounces the provider callback back to the app callback page with code
 * and state, and the app exchanges code and state for a JWT pair at
 * /v1/auth/login. The gateway is the confidential client: it holds every
 * provider secret and performs the code exchange. The app never sees a provider
 * secret, an id token, or the PKCE verifier.
 *
 * Token management mirrors the native app's interceptor and authenticator: a
 * single-flight refresh rotates a near-expiry access token before an authed read
 * (proactive) and rotates once on a 401 then replays the request (reactive). A
 * refresh that the gateway rejects clears the session and signals the UI to
 * return to the login screen.
 *
 * Because both of those are driven by an outbound request, neither can notice a
 * session that expired while the app sat idle. The expiry watchdog covers that: it
 * reads the refresh token's own expiry and ends the session on time whether or not
 * anything is being fetched, on a timer and on every return to the foreground.
 */

import {
  request,
  ApiError,
  type AuthRecoveryContext,
  type AuthRecoveryMode,
} from '@/lib/api';
import {
  storeLogin,
  storeReauthenticatedSession,
  storeRefreshedPair,
  getRefreshToken,
  getSessionMeta,
  getLocalSessionId,
  getLocalRefreshToken,
  sessionPersistenceWasInvalidated,
  getToken,
  getDeviceFingerprint,
  clearSession,
  clearStaleSessionLocally,
  isRealSession,
  isPasskeyReauthenticationRequired,
  requirePasskeyReauthentication,
  resumePersistedSession as resumePersistedSessionFromStorage,
  type LoginSession,
} from '@/lib/session';
import {
  readJwtAccountIdentity,
  readJwtExpSeconds,
  readJwtSessionId,
} from '@/lib/jwtClaims';
import { loginWithPasskey } from '@/lib/webauthn';
import { clearSwapIdentityToken } from '@/lib/swapToken';
import { clearPendingSwaps } from '@/store/store';

/** sessionStorage key holding the pending login record across the IdP redirect. */
const PENDING_KEY = 'pmw.oauth.pending';

/**
 * Custom event dispatched on window when a refresh is rejected and the session
 * is cleared, so the boot gate can route back to the login screen. The name is
 * the single coupling point between this module and the boot wiring.
 */
export const SESSION_EXPIRED_EVENT = 'pmw:session-expired';

/**
 * In-flight refresh promise, or null when no refresh is running. A module-level
 * single-flight guard so concurrent authed requests share one refresh rather
 * than each minting a new token pair and racing rotation. This is the browser
 * analog of the native app's refresh mutex.
 */
let inFlightRefresh: Promise<string | null> | null = null;

/**
 * One explicit passkey recovery shared by concurrent pre-signing requests. A
 * single browser prompt must resolve the session for all of them rather than
 * racing multiple WebAuthn ceremonies and token writes.
 */
interface PasskeyRecoveryFlight {
  /** One underlying refresh/passkey sequence shared by every current waiter. */
  promise: Promise<string>;
  /** Cancels the underlying sequence only after its final waiter cancels. */
  controller: AbortController;
  /** Callers still waiting for this sequence to finish. */
  waiters: number;
  /** True once the underlying sequence has resolved or rejected. */
  settled: boolean;
  /** Account and auth session that owned the request which opened this flight. */
  owner: PasskeySessionIdentity;
  /** Exact local and persisted session state captured when this flight started. */
  sessionOwner: SessionOwnerSnapshot;
  /** Mutable ownership transferred when an aborted flight gets a successor. */
  lifecycle: {
    /** True after the current refresh credential was proven unusable or absent. */
    authLossConfirmed: boolean;
    /** Whether this flight may end the session after passkey recovery fails. */
    ownsSessionTeardown: boolean;
  };
}

let inFlightPasskeyRecovery: PasskeyRecoveryFlight | null = null;

/** Internal marker: silent refresh failed transiently, so no passkey is asked. */
class SilentRefreshUnavailableError extends Error {
  constructor() {
    super('silent refresh is temporarily unavailable');
    this.name = 'SilentRefreshUnavailableError';
  }
}

/** A request no longer belongs to the auth session persisted by this browser. */
class SessionChangedError extends ApiError {
  constructor() {
    super('The authentication session changed. Retry from the active session.', {
      status: 401,
      code: 'SESSION_CHANGED',
    });
    this.name = 'SessionChangedError';
  }
}

/**
 * A refresh failure bound to the session generation that issued the request.
 * The structured ApiError fields are retained so hard rejection and retry
 * decisions remain unchanged, while a late response cannot tear down a newer
 * passkey-authenticated session.
 */
class SessionRefreshError extends ApiError {
  constructor(
    error: ApiError,
    readonly generationAtStart: number,
    readonly refreshTokenAtStart: string | null,
    readonly sessionIdAtStart: string | null,
  ) {
    super(error.message, {
      status: error.status,
      code: error.code,
      body: error.body,
      timeout: error.timeout,
      offline: error.offline,
      aborted: error.aborted,
    });
    this.name = 'SessionRefreshError';
  }
}

/**
 * Bumped when this module replaces a session through recovery or tears it down.
 * A refresh also captures the persisted refresh token and session id, covering
 * replacement paths that call the session store directly. A response that lands
 * after any of those owner fields changed is discarded instead of resurrecting
 * or deleting the wrong session.
 */
let sessionGeneration = 0;

/**
 * Stable boundary for one logical authenticated API request. Unlike
 * `sessionGeneration`, this does not advance for same-identity passkey recovery:
 * the user-authorized request that opened that ceremony may continue. Fresh
 * login and every local ownership loss do advance it, so old retries and 401
 * replays cannot cross into a replacement account.
 */
let authRequestGeneration = 0;

/** Immutable auth ownership captured by the API layer for one logical request. */
export interface AuthRequestScope {
  generation: number;
  realSession: boolean;
  /** False when shared persistence belongs to a different browser session. */
  ownerCurrent: boolean;
}

/** Return the current local auth ownership without exposing credentials. */
export function getAuthRequestScope(): AuthRequestScope {
  return {
    generation: authRequestGeneration,
    realSession: isRealSession(),
    ownerCurrent:
      !isRealSession() || localSessionMayContinue(classifyLocalSessionOwnership()),
  };
}

/**
 * Identity retained in memory when refresh storage disappears before the
 * watchdog suspends a passkey session. An expired stored refresh token is the
 * normal continuity source; this covers only the missing-token edge case.
 */
let pendingPasskeyIdentity: PasskeySessionIdentity | null = null;

/** One browser-wide lock serializing every mutation of the shared session. */
const SESSION_MUTATION_LOCK = 'plantmewallet-session-mutation';

/** Same-tab fallback queue for environments without the Web Locks API. */
let localSessionMutationTail: Promise<void> = Promise.resolve();

/** Stable owner fields captured before asynchronous auth work begins. */
interface SessionOwnerSnapshot {
  generation: number;
  refreshToken: string | null;
  sessionId: string | null;
}

/** Shared persistence identity, keeping invalid ownership distinct from absence. */
interface PersistedSessionOwner {
  sessionId: string | null;
  status: 'valid' | 'invalidated' | 'conflict' | 'legacy-sidless';
}

/** Relationship between this tab's stable owner and shared persistence. */
type LocalSessionOwnership =
  | 'current'
  | 'recoverable-successor'
  | 'replaced'
  | 'invalidated'
  | 'conflict'
  | 'legacy-sidless'
  | 'inactive';

/** Capture the complete local and persisted identity of the active session. */
function captureSessionOwner(): SessionOwnerSnapshot {
  const refreshToken = getRefreshToken();
  const persistedOwner = currentPersistedSessionOwner(refreshToken);
  return {
    generation: sessionGeneration,
    refreshToken,
    sessionId: persistedOwner.sessionId,
  };
}

/** Whether this browser can serialize session writes across tabs and workers. */
function hasCrossTabSessionLock(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.locks?.request === 'function'
  );
}

/** Build the fail-closed error for an unavailable browser-wide session lock. */
function sessionCoordinationUnavailable(): ApiError {
  return new ApiError(
    'This browser cannot safely coordinate authentication across tabs. Sign in again in a supported browser to continue.',
    { status: 401, code: 'SESSION_COORDINATION_UNAVAILABLE' },
  );
}

/**
 * Run one session operation under a same-tab queue and, when available, the
 * browser-wide Web Lock. Operations that consume or replace credentials require
 * the browser-wide lock because their ownership check, asynchronous work, and
 * credential write must be indivisible relative to other tabs.
 */
async function withSessionMutationLock<T>(
  operation: () => T | Promise<T>,
  requireCrossTab = false,
): Promise<T> {
  const previous = localSessionMutationTail;
  let release!: () => void;
  localSessionMutationTail = new Promise<void>((resolve) => {
    release = resolve;
  });

  await previous;
  try {
    if (!hasCrossTabSessionLock()) {
      if (requireCrossTab) {
        throw sessionCoordinationUnavailable();
      }
      // Await before releasing the same-tab queue: returning the promise directly
      // would run the finally block while an asynchronous mutation was in flight.
      return await operation();
    }

    let operationStarted = false;
    try {
      return await navigator.locks.request(
        SESSION_MUTATION_LOCK,
        { mode: 'exclusive' },
        async () => {
          operationStarted = true;
          return await operation();
        },
      );
    } catch (err) {
      // Presence of navigator.locks is not enough if this environment refuses
      // acquisition. Required operations must still fail before touching the
      // rotating credential; errors thrown by the operation itself retain their
      // original classification.
      if (requireCrossTab && !operationStarted) {
        throw sessionCoordinationUnavailable();
      }
      if (!operationStarted) {
        return await operation();
      }
      throw err;
    }
  } finally {
    release();
  }
}

/** The pending login record held across the IdP redirect. */
interface PendingLogin {
  /** Provider the flow was started for. */
  provider: string;
  /**
   * Server-side session id from init. Held client-side because it is
   * deliberately excluded from the callback URL and is what binds the callback
   * to this client.
   */
  oauthSessionId: string;
  /**
   * The state value from the init authorization URL, checked against the state
   * the IdP returns to defeat CSRF.
   */
  expectedState: string;
  /**
   * The org id this flow requested at init. It is the self-service sentinel for a
   * normal registration and a specific org id when the user is joining a shared
   * org through an invite or admin link. It is held across the redirect so the
   * login call sends the same value the init used, keeping the two halves of the
   * flow consistent. The gateway still enforces membership for any non-sentinel
   * org, so carrying it here cannot widen access.
   */
  requestedOrgId: string;
  /** When the pending record stops being valid, in milliseconds since epoch. */
  expiresAtMillis: number;
}

/** Gateway response from the OAuth init endpoint. */
interface OauthInitResponse {
  oauth_session_id: string;
  authorization_url: string;
  expires_in_seconds: number;
}

/** Gateway response carrying a minted or rotated JWT pair. */
interface TokenPairResponse {
  access_token: string;
  refresh_token: string;
  session_id?: string;
  /**
   * The org the token was scoped to. For a self-service signup this is the
   * caller's server-derived personal org, which the client records and uses for
   * subsequent requests rather than a hardcoded default. Absent on a refresh
   * response, which does not re-scope the session.
   */
  org_id?: string;
}

/**
 * Sentinel org id the client sends to request self-service signup. The gateway
 * resolves it to the caller's own server-derived personal org rather than
 * treating it as a named shared org. It is the nil UUID, which is never a real
 * org id, so it cannot collide with a shared org. A first-time user signs in
 * with this value and receives their personal org back in the login response.
 */
const SELF_SERVICE_ORG_SENTINEL = '00000000-0000-0000-0000-000000000000';

/** Matches a canonical lower or upper case UUID, the shape of a real org id. */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Resolve the org id to request for this sign-in.
 *
 * A normal visitor registers into their own personal org, so the default is the
 * self-service sentinel. A user joining a shared org arrives through a link that
 * names it, for example an admin console or an invite at
 * `https://test.plantmewallet.com/?org=<uuid>`; when the current URL carries a
 * well-formed `org` query parameter that is not the sentinel, that org is
 * requested instead. Sending a shared org is safe because the gateway still
 * requires an active membership for any non-sentinel org and refuses a
 * non-member, so this only selects which membership the login is checked
 * against, never grants one. A malformed value falls back to the sentinel rather
 * than being forwarded as a bogus org.
 *
 * The URL query string is a parameter defaulting to the live location so the
 * decision can be unit tested without a DOM. The `window` reference is only
 * evaluated when the argument is omitted, which is the runtime path.
 */
export function resolveRequestedOrgId(search: string = window.location.search): string {
  const raw = new URLSearchParams(search).get('org');
  if (raw === null) {
    return SELF_SERVICE_ORG_SENTINEL;
  }
  const candidate = raw.trim().toLowerCase();
  return UUID_PATTERN.test(candidate) ? candidate : SELF_SERVICE_ORG_SENTINEL;
}

/**
 * Normalize an org id typed on the sign-in screen to the canonical lower-case
 * UUID the gateway expects, or null when it is blank or not a UUID. An admin
 * uses the sign-in field to target a shared org directly, which is more reliable
 * than a URL query parameter that a redirect can drop before the login runs.
 * Sending a shared org is safe: the gateway still requires an active membership
 * and refuses a non-member, so this only chooses which membership the login is
 * checked against.
 *
 * @param raw The user-entered org id.
 * @returns The normalized UUID, or null when the entry is blank or malformed.
 */
export function normalizeOrgId(raw: string): string | null {
  const candidate = raw.trim().toLowerCase();
  return UUID_PATTERN.test(candidate) ? candidate : null;
}

/** Callback query parameters the gateway echoes back to the callback page. */
export interface CompleteLoginParams {
  /** Provider name the gateway echoed. */
  provider?: string;
  /** Authorization code from the IdP. */
  code?: string;
  /** State value the IdP returned. */
  state?: string;
  /** Error code when the IdP denied the request. */
  error?: string;
}

/**
 * Begin sign-in for a provider. Allocates a server-side OAuth session, stashes
 * the pending record so the callback can complete the flow, and navigates the
 * browser to the provider's authorization URL.
 *
 * @param provider Provider name, for example "google".
 * @returns Resolves only if navigation does not occur; normally the call ends by
 *   navigating away.
 * @throws {ApiError} When init fails, for example an unregistered provider.
 */
export async function beginLogin(provider: string, orgOverride?: string): Promise<void> {
  // Choose the org to sign in to, in order of precedence: an org typed on the
  // sign-in screen (the admin path, most reliable since it does not depend on a
  // URL surviving the login navigation), then a well-formed org query parameter
  // on the current URL, then the self-service sentinel so a first-time user signs
  // in to their own personal org. The value is held in the pending record so the
  // later login call sends the same org this init used.
  const typedOrg = orgOverride ? normalizeOrgId(orgOverride) : null;
  const requestedOrgId = typedOrg ?? resolveRequestedOrgId();

  const init = await request<OauthInitResponse>('/v1/auth/oauth/init', {
    method: 'POST',
    auth: false,
    body: { provider, org_id: requestedOrgId, client_type: 'web' },
  });

  const authUrl = init.authorization_url;
  const expectedState = new URL(authUrl).searchParams.get('state') ?? '';
  const ttlSeconds = Number.isFinite(init.expires_in_seconds) ? init.expires_in_seconds : 600;

  const pending: PendingLogin = {
    provider,
    oauthSessionId: init.oauth_session_id,
    expectedState,
    requestedOrgId,
    expiresAtMillis: Date.now() + ttlSeconds * 1000,
  };
  writePending(pending);

  // Leave the SPA entirely: the authorization URL is on the provider's origin.
  // sessionStorage survives this top-level navigation away and back, which is
  // why the pending record lives there rather than in a module variable.
  window.location.assign(authUrl);
}

/**
 * Complete sign-in from the callback page. Reads and clears the pending record,
 * verifies the returned state against it, exchanges code and state for a JWT
 * pair, and records the session. The single-use code and the CSRF state binding
 * are both enforced by the gateway; this checks state locally first so a forged
 * callback never reaches the exchange.
 *
 * @param params Callback query parameters.
 * @returns Resolves once the session is stored.
 * @throws {Error} With a user-facing message on any failure.
 */
export async function completeLogin({ provider, code, state, error }: CompleteLoginParams): Promise<void> {
  const pending = readPending();

  // An IdP denial carries an error rather than a code. Surface it and stop
  // before touching the pending record's single-use session.
  if (error && error.trim() !== '') {
    clearPending();
    throw new Error(`Sign-in was cancelled or denied (${error}).`);
  }

  if (!pending) {
    throw new Error('No sign-in is in progress. Start again from the sign-in screen.');
  }
  if (Date.now() > pending.expiresAtMillis) {
    clearPending();
    throw new Error('This sign-in attempt expired. Start again from the sign-in screen.');
  }
  if (!code || code.trim() === '' || !state || state.trim() === '') {
    throw new Error('The sign-in response was incomplete. Start again from the sign-in screen.');
  }

  // Compare the returned state to the expected state without clearing the
  // pending record on mismatch, so a forged callback cannot burn the one-shot
  // session and lock out the real redirect that may still arrive.
  if (state !== pending.expectedState) {
    throw new Error('The sign-in response failed a security check. Start again from the sign-in screen.');
  }

  // The state checked out, so this is the genuine redirect. Clear the pending
  // record now to make the exchange single-use from the client side too.
  clearPending();

  const resolvedProvider = provider && provider.trim() !== '' ? provider : pending.provider;

  const login = await request<TokenPairResponse>('/v1/auth/login', {
    method: 'POST',
    auth: false,
    body: {
      provider: resolvedProvider,
      // Send the same org the init used. For a sentinel this requests
      // self-service signup, and the gateway resolves the caller's own personal
      // org from the verified identity and returns it below: a first-time user is
      // enrolled into their personal org and a returning user resolves the
      // existing membership. For a named shared org the gateway checks membership
      // and refuses a non-member, so this never grants access on its own.
      org_id: pending.requestedOrgId,
      oauth_session_id: pending.oauthSessionId,
      code,
      state,
      device_fingerprint: getDeviceFingerprint(),
    },
  });

  // Record the org the gateway scoped the token to, taken from the login
  // response rather than the value we sent, so the session tracks the
  // server-derived personal org for a self-service signup. Fall back to the
  // requested org only if an older gateway omits the field; the org that
  // authorizes reads still lives in the signed token claim regardless.
  await establishSession({
    accessToken: login.access_token,
    refreshToken: login.refresh_token,
    sessionId: login.session_id,
    orgId: login.org_id ?? pending.requestedOrgId,
    provider: resolvedProvider,
  });
}

/**
 * Install a newly authenticated session under the browser-wide mutation lock.
 * A login is unconditional: whichever login commits last is the active account,
 * while an older refresh or recovery must re-check its captured owner and yield.
 */
export async function establishSession(session: LoginSession): Promise<void> {
  await withSessionMutationLock(() => {
    sessionGeneration += 1;
    authRequestGeneration += 1;
    pendingPasskeyIdentity = null;
    storeLogin(session);
  });
}

/**
 * Resume persisted authentication without racing another tab's session write.
 *
 * A marker alone cannot prove that the refresh value predates it: a newer login
 * can persist its refresh while its metadata overwrite fails. Boot therefore
 * never removes marker-protected credentials. With Web Locks, the resume decision
 * still shares the same critical section as login, refresh, reauthentication,
 * logout, and expiry so it observes a complete successful publication.
 */
export async function resumePersistedAuthSession(): Promise<boolean> {
  const resume = (): boolean => {
    const wasRealSession = isRealSession();
    const resumed = resumePersistedSessionFromStorage();
    if (resumed && !wasRealSession) authRequestGeneration += 1;
    return resumed;
  };
  if (!hasCrossTabSessionLock()) return resume();

  try {
    return await withSessionMutationLock(
      resume,
      true,
    );
  } catch (err) {
    if (err instanceof ApiError && err.code === 'SESSION_COORDINATION_UNAVAILABLE') {
      return resume();
    }
    throw err;
  }
}

/**
 * Proactively refresh the access token when it is within a leeway of expiry.
 * Installed on the api layer so an authed request always carries a token with
 * headroom. A no-op when no refresh token is stored or the current token is
 * still fresh.
 *
 * @param leewaySeconds Refresh when the token expires within this many seconds.
 *   Defaults to sixty.
 * @param recovery Whether an explicit pre-signing request may fall back to
 *   passkey authentication after a hard refresh rejection.
 * @param signal Optional caller cancellation for that passkey recovery wait.
 * @returns Resolves once any needed refresh settles. Ordinary transport failures
 *   are swallowed; unsafe cross-tab coordination and a failed permitted passkey
 *   ceremony reject before the authenticated request is sent.
 */
export async function ensureFreshAccessToken(
  leewaySeconds = 60,
  recovery: AuthRecoveryMode = 'refresh-only',
  signal?: AbortSignal,
): Promise<void> {
  // Shared refresh persistence is not authority to activate it in this tab.
  // Only explicit boot resume or login may create a real local session.
  if (!isRealSession()) return;
  const generationAtStart = sessionGeneration;
  if (!localSessionMayContinue(classifyLocalSessionOwnership())) {
    await discardStaleLocalSession(generationAtStart);
    throw new SessionChangedError();
  }
  const activeToken = getToken();
  if (activeToken && requestSessionConflictsWithPersistence(activeToken)) {
    await discardStaleLocalSession(sessionGeneration);
    throw new SessionChangedError();
  }
  const refreshToken = getRefreshToken();
  const expectedIdentity = recoverablePasskeyIdentity();
  if (isPasskeyReauthenticationRequired()) {
    if (recovery === 'passkey' && expectedIdentity) {
      await recoverAccessWithPasskey(expectedIdentity, signal);
      return;
    }
    throw new ApiError('Passkey verification is required to continue.', {
      status: 401,
      code: 'PASSKEY_REAUTHENTICATION_REQUIRED',
    });
  }

  // A healthy in-memory access token needs neither refresh nor recovery. Check
  // this before the missing-refresh branch so a transiently absent storage value
  // cannot open a passkey prompt while the current token is still valid.
  if (!shouldRefresh(leewaySeconds)) return;

  // An explicit signing request already owns session recovery. A background or
  // refresh-only preflight must not start another rotating-token request while
  // WebAuthn is open: that request would hold the mutation lock needed to commit
  // the recovered session. The explicit caller will publish the outcome.
  if (inFlightPasskeyRecovery && recovery !== 'passkey') return;

  if (!refreshToken) {
    if (recovery === 'passkey' && expectedIdentity) {
      await recoverAccessWithPasskey(expectedIdentity, signal);
    }
    return;
  }

  if (recovery === 'passkey' && expectedIdentity) {
    try {
      await recoverAccessWithPasskey(expectedIdentity, signal);
    } catch (err) {
      // A transient refresh failure keeps the pre-existing behavior: try the
      // request with the current token and let its transport result decide. Once
      // refresh was authoritatively rejected, however, any passkey error stops
      // the fund-moving request before it reaches the gateway.
      if (err instanceof SilentRefreshUnavailableError) return;
      throw err;
    }
    return;
  }

  try {
    await refreshSingleFlight(readJwtSessionId(activeToken) ?? getLocalSessionId());
  } catch (err) {
    if (isSessionChanged(err)) {
      await discardStaleLocalSession(generationAtStart);
      throw new SessionChangedError();
    }
    if (isSessionCoordinationUnavailable(err)) throw err;
    if (isSessionPersistenceFailure(err)) throw err;
    // A hard rejection ends an ordinary session. Do not race an explicit
    // passkey recovery already in progress: that request owns the replacement
    // session and will either install it or end the session itself.
    const rejectedOwner = refreshFailureOwner(err);
    if (isHardRefreshRejection(err) && rejectedOwner) {
      await suspendOrEndSessionAfterAuthLoss(
        rejectedOwner,
        () => !inFlightPasskeyRecovery && isRealSession(),
      );
    }
    // Other proactive failures are swallowed: the request that follows runs
    // with whatever token is present and surfaces its own transport result.
  }
}

/**
 * Reactively refresh after a 401, used by the api layer to rotate once and let
 * the request replay. Returns the fresh access token on success so the caller
 * can replay with it, or null when the session could not be refreshed.
 *
 * @param failedToken The exact token attached to the failed request, used to
 *   distinguish a concurrent same-account rotation from an account switch.
 * @param context The request's permitted recovery and concurrent-request state.
 * @returns A fresh access token, or null when refresh failed and the session was
 *   cleared. Rejects when safe cross-tab coordination is unavailable.
 */
export async function refreshAfter401(
  failedToken: string | null,
  context: AuthRecoveryContext = {
    mode: 'refresh-only',
  },
): Promise<string | null> {
  // A stale logical request must never adopt a peer tab's preserved refresh
  // credential after this tab has already discarded its local session.
  if (!isRealSession()) return null;
  const generationAtStart = sessionGeneration;
  if (!localSessionMayContinue(classifyLocalSessionOwnership())) {
    await discardStaleLocalSession(generationAtStart);
    return null;
  }
  const failedSessionId = readJwtSessionId(failedToken) ?? getLocalSessionId();
  const persistedOwner = currentPersistedSessionOwner();
  const persistedSessionId = persistedOwner.sessionId;
  // A shared refresh token may already belong to a newer login while this tab
  // still holds the previous access token in memory. Never spend it for the
  // request unless account, signed sid, and persisted metadata still agree.
  if (
    persistedOwner.status !== 'valid' ||
    (failedToken && requestSessionConflictsWithPersistence(failedToken))
  ) {
    await discardStaleLocalSession(sessionGeneration);
    return null;
  }

  // A concurrent request may already have rotated the token after this request
  // was sent. Replay with that token instead of spending the newly rotated
  // refresh token again. The api layer passes the exact token attached to the
  // failed request, not a fresh read taken after the response arrived.
  const currentToken = getToken();
  if (failedToken && currentToken && failedToken !== currentToken) {
    // A rotation for the same subject is safe to replay. An account switch is
    // not: the request was authored under the previous identity and must never
    // be replayed merely because another token is now present.
    return sameJwtSession(failedToken, currentToken) &&
      !requestSessionConflictsWithPersistence(currentToken)
      ? currentToken
      : null;
  }

  const expectedIdentity = recoverablePasskeyIdentity();
  // Do not let an ordinary request consume or lock the old refresh credential
  // while an explicit passkey recovery is already replacing it. A passkey-safe
  // caller joins the shared recovery; every other caller stops without replay.
  if (inFlightPasskeyRecovery) {
    return context.mode === 'passkey' && expectedIdentity
      ? recoverAccessWithPasskey(expectedIdentity, context.signal)
      : null;
  }
  if (isPasskeyReauthenticationRequired()) {
    if (context.mode === 'passkey' && expectedIdentity) {
      return recoverAccessWithPasskey(expectedIdentity, context.signal);
    }
    return null;
  }
  if (context.mode === 'passkey' && expectedIdentity) {
    try {
      return await recoverAccessWithPasskey(expectedIdentity, context.signal);
    } catch (err) {
      // A network failure while attempting silent refresh is not evidence that
      // the refresh token is dead and must not open a passkey prompt. Surface the
      // original 401 without destroying the still-recoverable session.
      if (err instanceof SilentRefreshUnavailableError) return null;
      throw err;
    }
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    // A 401 with no refresh token to rotate is terminal, and until this branch
    // existed it returned null without clearing anything: the account slice
    // stayed authenticated against a dead token, so the app sat on the current
    // screen erroring instead of routing to sign-in. The guard on isRealSession
    // is what keeps the legacy dev-token fallback out of it — that path never had
    // a session to end, and tearing one down there would only bounce a developer
    // through the sign-in screen and straight back.
    const ownerWithoutRefresh = captureSessionOwner();
    await suspendOrEndSessionAfterAuthLoss(
      ownerWithoutRefresh,
      () => isRealSession() && !inFlightPasskeyRecovery,
    );
    return null;
  }
  try {
    return await refreshSingleFlight(failedSessionId ?? persistedSessionId);
  } catch (err) {
    if (isSessionChanged(err)) {
      await discardStaleLocalSession(generationAtStart);
      return null;
    }
    if (isSessionCoordinationUnavailable(err)) throw err;
    if (isSessionPersistenceFailure(err)) throw err;
    const rejectedOwner = refreshFailureOwner(err);
    if (isHardRefreshRejection(err) && rejectedOwner) {
      await suspendOrEndSessionAfterAuthLoss(
        rejectedOwner,
        () => isRealSession() && !inFlightPasskeyRecovery,
      );
    }
    // Returning null tells the api layer not to replay. Transient failures leave
    // the session intact; hard failures end it unless an explicit passkey-safe
    // request is concurrently responsible for recovery.
    return null;
  }
}

/** Identity that must survive a usernameless passkey reauthentication. */
interface PasskeySessionIdentity {
  orgId: string;
  userId: string;
  sessionId: string | null;
}

/** Return the current passkey identity, or null when it cannot be bound safely. */
function recoverablePasskeyIdentity(): PasskeySessionIdentity | null {
  if (!isRealSession()) return null;
  const meta = getSessionMeta();
  if (meta?.provider !== 'passkey' || !meta.orgId || !UUID_PATTERN.test(meta.orgId)) {
    return null;
  }
  if (isPasskeyReauthenticationRequired() && pendingPasskeyIdentity) {
    if (pendingPasskeyIdentity.orgId !== meta.orgId.toLowerCase()) return null;
    if (pendingPasskeyIdentity.sessionId === meta.sessionId) {
      return pendingPasskeyIdentity;
    }

    // Another tab may already have recovered the same account and published a
    // new server session while this tab remained suspended without an access
    // token. Accept only a refresh owner carrying the retained user and org and
    // matching the new persisted sid. Silent refresh then verifies that token at
    // the gateway; a different account can never redefine the retained identity.
    const peerRefreshIdentity = jwtIdentity(getRefreshToken());
    if (
      peerRefreshIdentity?.sessionId &&
      peerRefreshIdentity.sessionId === meta.sessionId &&
      identitiesEqual(peerRefreshIdentity, pendingPasskeyIdentity)
    ) {
      return {
        ...pendingPasskeyIdentity,
        sessionId: peerRefreshIdentity.sessionId,
      };
    }
    return null;
  }
  // Both access and refresh JWTs carry issuer-signed sub and org_id claims. The
  // client reads them without verifying only as a continuity check; the gateway
  // verifies every token and the new passkey assertion authoritatively. When
  // both tokens exist they must agree, or recovery fails closed.
  const accessToken = getToken();
  const refreshToken = getRefreshToken();
  const accessIdentity = jwtIdentity(accessToken);
  const refreshIdentity = jwtIdentity(refreshToken);
  const accessSessionId = accessIdentity
    ? accessIdentity.sessionId ?? getLocalSessionId()
    : null;
  const refreshSessionId = readJwtSessionId(refreshToken);
  if (
    accessIdentity &&
    refreshIdentity &&
    (!identitiesEqual(accessIdentity, refreshIdentity) ||
      accessSessionId !== refreshSessionId)
  ) {
    return null;
  }
  const identity = accessIdentity ?? refreshIdentity;
  const tokenSessionId = accessSessionId ?? refreshSessionId;
  if (
    !identity ||
    identity.orgId !== meta.orgId.toLowerCase() ||
    !tokenSessionId ||
    tokenSessionId !== meta.sessionId
  ) {
    return null;
  }
  return { ...identity, sessionId: tokenSessionId };
}

/** Read the subject, org, and stable sid claims used to bind one client session. */
function jwtIdentity(token: string | null): PasskeySessionIdentity | null {
  const identity = readJwtAccountIdentity(token);
  return identity
    ? { ...identity, sessionId: readJwtSessionId(token) }
    : null;
}

/** Compare the complete account identity carried by two session tokens. */
function identitiesEqual(
  left: PasskeySessionIdentity,
  right: PasskeySessionIdentity,
): boolean {
  return left.orgId === right.orgId && left.userId === right.userId;
}

/** True when two readable JWTs belong to the same user, org, and server session. */
function sameJwtSession(leftToken: string, rightToken: string): boolean {
  const left = jwtIdentity(leftToken);
  const right = jwtIdentity(rightToken);
  return Boolean(
    left &&
    right &&
    identitiesEqual(left, right) &&
    left.sessionId &&
    left.sessionId === right.sessionId,
  );
}

/** Whether a request token conflicts with the refresh owner persisted now. */
function requestSessionConflictsWithPersistence(token: string): boolean {
  // The legacy development token has no OAuth persistence owner. An ended OAuth
  // marker must not disable that explicitly supported fallback after logout.
  if (!isRealSession()) return false;

  const refreshToken = getRefreshToken();
  const persistedOwner = currentPersistedSessionOwner(refreshToken);
  if (persistedOwner.status !== 'valid') return true;

  const requestIdentity = jwtIdentity(token);
  if (!requestIdentity) return true;

  // The access token may predate sid-bearing JWTs, but its login response still
  // bound it to an exact session. Never infer that owner from current shared
  // metadata: another tab may already have replaced both persisted records.
  const requestSessionId = requestIdentity.sessionId ?? getLocalSessionId();
  if (!requestSessionId) return true;

  const meta = getSessionMeta();
  if (
    (meta?.sessionId && requestSessionId !== meta.sessionId) ||
    (meta?.orgId && requestIdentity.orgId !== meta.orgId.toLowerCase())
  ) {
    return true;
  }

  // A healthy access token can outlive missing refresh storage only when
  // metadata still binds it to the current session. If both owners disappeared,
  // another tab may have logged out and the request must stop. Re-read metadata
  // after observing the missing refresh so a failed metadata overwrite cannot
  // keep a cached owner alive across that removal.
  if (!refreshToken) return !getSessionMeta()?.sessionId;
  const refreshIdentity = jwtIdentity(refreshToken);
  return (
    !refreshIdentity ||
    !identitiesEqual(requestIdentity, refreshIdentity) ||
    refreshIdentity.sessionId !== requestSessionId
  );
}

/** Resolve shared ownership without conflating an invalid state with no sid. */
function currentPersistedSessionOwner(
  refreshToken: string | null = getRefreshToken(),
): PersistedSessionOwner {
  const metadataSessionId = getSessionMeta()?.sessionId ?? null;
  if (sessionPersistenceWasInvalidated()) {
    return { sessionId: null, status: 'invalidated' };
  }
  const refreshSessionId = readJwtSessionId(refreshToken);
  // A pre-migration refresh token cannot prove which revocable server session
  // it belongs to. The issuer refuses these tokens rather than extending a
  // sessionless refresh chain, so retire the current browser session once
  // metadata reveals this legacy state instead of looping SESSION_CHANGED.
  if (refreshToken && metadataSessionId && !refreshSessionId) {
    return refreshToken === getLocalRefreshToken() &&
      metadataSessionId === getLocalSessionId()
      ? { sessionId: metadataSessionId, status: 'legacy-sidless' }
      : { sessionId: null, status: 'conflict' };
  }
  if (
    metadataSessionId &&
    refreshSessionId &&
    metadataSessionId !== refreshSessionId
  ) {
    return { sessionId: null, status: 'conflict' };
  }
  return {
    sessionId: metadataSessionId ?? refreshSessionId,
    status: 'valid',
  };
}

/** Whether an ownership state may continue this tab's existing request. */
function localSessionMayContinue(ownership: LocalSessionOwnership): boolean {
  return ownership === 'current' || ownership === 'recoverable-successor';
}

/**
 * Classify shared persistence against the owner this tab explicitly adopted.
 *
 * The local session id and refresh credential never follow localStorage reads,
 * so a peer login cannot redefine this tab while its access token is absent.
 * A suspended passkey session has one narrow exception: another tab may publish
 * a new server session for the same retained user and org, which an explicit
 * passkey-safe request may silently verify and adopt.
 */
function classifyLocalSessionOwnership(): LocalSessionOwnership {
  if (!isRealSession()) return 'inactive';

  const persistedRefreshToken = getRefreshToken();
  const persistedOwner = currentPersistedSessionOwner(persistedRefreshToken);
  if (persistedOwner.status === 'invalidated') return 'invalidated';
  if (persistedOwner.status === 'conflict') return 'conflict';
  if (persistedOwner.status === 'legacy-sidless') return 'legacy-sidless';

  const localSessionId = getLocalSessionId();
  const localRefreshToken = getLocalRefreshToken();
  if (localSessionId && persistedOwner.sessionId) {
    if (localSessionId === persistedOwner.sessionId) {
      const localIdentity = jwtIdentity(localRefreshToken);
      const persistedIdentity = jwtIdentity(persistedRefreshToken);
      return localIdentity &&
        persistedIdentity &&
        !identitiesEqual(localIdentity, persistedIdentity)
        ? 'replaced'
        : 'current';
    }
  } else if (
    !localSessionId &&
    !persistedOwner.sessionId &&
    localRefreshToken &&
    localRefreshToken === persistedRefreshToken
  ) {
    return 'current';
  }

  if (isPasskeyReauthenticationRequired() && pendingPasskeyIdentity) {
    const successor = recoverablePasskeyIdentity();
    if (
      successor?.sessionId &&
      successor.sessionId === persistedOwner.sessionId &&
      successor.sessionId !== localSessionId
    ) {
      return 'recoverable-successor';
    }
  }

  return 'replaced';
}

/** True for a client-detected auth-session ownership change, never a gateway 401. */
function isSessionChanged(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'SESSION_CHANGED';
}

/** True only for an authoritative refresh-token rejection. */
function isHardRefreshRejection(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 401 &&
    !isSessionChanged(err) &&
    err.code !== 'SESSION_COORDINATION_UNAVAILABLE'
  );
}

/** True when this browser cannot safely serialize a rotating refresh token. */
function isSessionCoordinationUnavailable(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'SESSION_COORDINATION_UNAVAILABLE';
}

/** True when a consumed refresh token's successor could not be shared safely. */
function isSessionPersistenceFailure(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'SESSION_PERSISTENCE_FAILED';
}

/** Recover the owner captured by a structured refresh failure. */
function refreshFailureOwner(err: unknown): SessionOwnerSnapshot | null {
  if (!(err instanceof SessionRefreshError)) return null;
  return {
    generation: err.generationAtStart,
    refreshToken: err.refreshTokenAtStart,
    sessionId: err.sessionIdAtStart,
  };
}

/** Match all stable fields that identify the session which issued a refresh. */
function refreshOwnerIsCurrent(
  generationAtStart: number,
  refreshTokenAtStart: string | null,
  sessionIdAtStart: string | null,
): boolean {
  const currentRefreshToken = getRefreshToken();
  const currentOwner = currentPersistedSessionOwner(currentRefreshToken);
  return (
    currentOwner.status === 'valid' &&
    generationAtStart === sessionGeneration &&
    refreshTokenAtStart === currentRefreshToken &&
    sessionIdAtStart === currentOwner.sessionId
  );
}

/** Match a captured session owner against both local and shared state. */
function sessionOwnerIsCurrent(owner: SessionOwnerSnapshot): boolean {
  return refreshOwnerIsCurrent(
    owner.generation,
    owner.refreshToken,
    owner.sessionId,
  );
}

/**
 * Match the session lineage that an explicit logout started from.
 *
 * A proactive or concurrent refresh may rotate the refresh token while
 * `/v1/auth/logout` is in flight, but it cannot change the gateway's stable
 * session id. A real login changes that id (and a same-tab login also changes
 * the generation), so logout can clear its own rotation without deleting a
 * genuinely newer session. Fall back to the exact owner match when either side
 * has no session id; without that stable identifier a token change cannot be
 * classified safely as a rotation.
 */
function sessionLineageIsCurrent(owner: SessionOwnerSnapshot): boolean {
  if (owner.generation !== sessionGeneration) return false;
  const currentRefreshToken = getRefreshToken();
  const currentOwner = currentPersistedSessionOwner(currentRefreshToken);
  if (currentOwner.status === 'conflict') return false;
  const currentSessionId = currentOwner.sessionId;
  if (owner.sessionId && currentSessionId) {
    return owner.sessionId === currentSessionId;
  }
  // Another tab may finish logout first and remove both shared owner records.
  // With no replacement owner left to preserve, this tab must still complete
  // its explicit local logout. The Web Lock serializes this check against a new
  // login, and unavailable storage cannot be erased by the best-effort writes.
  if (owner.sessionId && !currentRefreshToken) return true;
  return sessionOwnerIsCurrent(owner);
}

/**
 * Refresh silently, falling back to one passkey login only after a hard refresh
 * rejection (or a missing refresh token). The passkey result is bound to the
 * previous org and user before it is stored, preventing a credential picker
 * choice from changing identities and replaying the caller's transaction.
 */
function startPasskeyRecovery(
  expected: PasskeySessionIdentity,
  inheritedAuthLoss = false,
): PasskeyRecoveryFlight {
  const ownerAtStart = captureSessionOwner();
  const controller = new AbortController();
  const lifecycle = {
    authLossConfirmed: inheritedAuthLoss || ownerAtStart.refreshToken === null,
    ownsSessionTeardown: true,
  };
  const recovery = (async (): Promise<string> => {
    if (
      expected.sessionId &&
      (ownerAtStart.sessionId !== expected.sessionId ||
        (ownerAtStart.refreshToken !== null &&
          readJwtSessionId(ownerAtStart.refreshToken) !== expected.sessionId))
    ) {
      throw new SessionChangedError();
    }
    if (ownerAtStart.refreshToken && !lifecycle.authLossConfirmed) {
      try {
        const refreshed = await refreshSingleFlight(expected.sessionId);
        if (refreshed) return refreshed;
      } catch (err) {
        if (isSessionChanged(err)) throw err;
        if (isSessionCoordinationUnavailable(err)) throw err;
        if (isSessionPersistenceFailure(err)) throw err;
        if (!isHardRefreshRejection(err)) {
          throw new SilentRefreshUnavailableError();
        }
        lifecycle.authLossConfirmed = true;
        // A rejected refresh is exactly the point at which the explicit
        // pre-signing request may ask the passkey to establish a new session.
      }
    }
    // A missing refresh token, a hard rejection, or a refresh response without
    // an access token all require explicit passkey recovery from this point.
    lifecycle.authLossConfirmed = true;

    // Logout or another session replacement won while refresh was in flight.
    // Never open or record a passkey login for a request that no longer owns the
    // active session.
    if (!sessionOwnerIsCurrent(ownerAtStart)) {
      throw new ApiError('The session changed before passkey verification completed.', {
        status: 401,
        code: 'SESSION_CHANGED',
      });
    }

    // Without a browser-wide lock, another tab could replace the session between
    // the final owner check and the credential write. Do not open a WebAuthn
    // prompt when its result cannot be committed safely.
    if (!hasCrossTabSessionLock()) {
      throw sessionCoordinationUnavailable();
    }

    try {
      if (controller.signal.aborted) throw passkeyRecoveryCancelled();
      const login = await loginWithPasskey({ signal: controller.signal });
      if (controller.signal.aborted) throw passkeyRecoveryCancelled();
      const returnedOrgId = login.orgId.trim().toLowerCase();
      const returnedAccessIdentity = jwtIdentity(login.accessToken);
      const returnedRefreshIdentity = jwtIdentity(login.refreshToken);
      if (
        returnedOrgId !== expected.orgId ||
        !returnedAccessIdentity ||
        !returnedRefreshIdentity ||
        !identitiesEqual(returnedAccessIdentity, expected) ||
        !identitiesEqual(returnedRefreshIdentity, expected) ||
        !returnedAccessIdentity.sessionId ||
        returnedAccessIdentity.sessionId !== returnedRefreshIdentity.sessionId ||
        returnedAccessIdentity.sessionId !== login.sessionId
      ) {
        throw new ApiError(
          'The selected passkey belongs to a different account. Sign in again with the original account.',
          { status: 401, code: 'PASSKEY_ACCOUNT_MISMATCH' },
        );
      }
      if (login.provider !== 'passkey' || login.accessToken.trim() === '') {
        throw new ApiError('Passkey verification did not return a valid session.', {
          status: 401,
          code: 'INVALID_PASSKEY_SESSION',
        });
      }
      const committed = await withSessionMutationLock(() => {
        if (controller.signal.aborted) throw passkeyRecoveryCancelled();
        if (!sessionOwnerIsCurrent(ownerAtStart)) return false;

        // This is a new server session for the SAME identity. Invalidate older
        // async auth work and session-bound swap identity. This is same-account
        // continuity, so keep the existing client session epoch and device list;
        // resetting them here would discard the recovered device-list response.
        sessionGeneration += 1;
        pendingPasskeyIdentity = null;
        clearSwapIdentityToken();
        storeReauthenticatedSession(login);
        return true;
      }, true);

      if (!committed) {
        throw new ApiError('The session changed during passkey verification.', {
          status: 401,
          code: 'SESSION_CHANGED',
        });
      }
      return login.accessToken;
    } catch (err) {
      // The old refresh token is unusable. A cancelled, failed, or mismatched
      // passkey leaves no session that may safely continue the transaction.
      await endSessionLocally(ownerAtStart, {
        additionalGuard: () => lifecycle.ownsSessionTeardown,
        requireCrossTab: true,
      });
      // Normalize browser ceremony errors to a non-retryable auth error. GET
      // helpers retry transport failures by default; treating a cancellation as
      // status 0 would reopen the passkey prompt up to three times.
      if (err instanceof ApiError) throw err;
      throw new ApiError(
        err instanceof Error ? err.message : 'Passkey verification failed.',
        { status: 401, code: 'PASSKEY_REAUTHENTICATION_FAILED' },
      );
    }
  })();

  const flight: PasskeyRecoveryFlight = {
    promise: recovery,
    controller,
    waiters: 0,
    settled: false,
    owner: expected,
    sessionOwner: ownerAtStart,
    lifecycle,
  };
  inFlightPasskeyRecovery = flight;
  void recovery.then(
    () => {
      flight.settled = true;
      if (inFlightPasskeyRecovery === flight) inFlightPasskeyRecovery = null;
    },
    () => {
      flight.settled = true;
      if (inFlightPasskeyRecovery === flight) inFlightPasskeyRecovery = null;
    },
  );
  return flight;
}

/** Build the non-retryable cancellation returned to one aborted caller. */
function passkeyRecoveryCancelled(): ApiError {
  return new ApiError('Passkey recovery was cancelled.', {
    status: 0,
    code: 'PASSKEY_RECOVERY_CANCELLED',
    aborted: true,
  });
}

/**
 * Wait for a shared passkey recovery under caller-specific cancellation.
 * Canceling one waiter rejects only that waiter. The underlying browser
 * ceremony is aborted when no waiter remains, so it cannot later install a
 * session for a request that no longer exists.
 */
function waitForPasskeyRecovery(
  flight: PasskeyRecoveryFlight,
  signal?: AbortSignal,
): Promise<string> {
  if (signal?.aborted) return Promise.reject(passkeyRecoveryCancelled());

  flight.waiters += 1;
  return new Promise<string>((resolve, reject) => {
    let finished = false;

    const finish = (): boolean => {
      if (finished) return false;
      finished = true;
      signal?.removeEventListener('abort', onAbort);
      flight.waiters -= 1;
      return true;
    };

    const onAbort = (): void => {
      if (!finish()) return;
      if (!flight.settled && flight.waiters === 0) flight.controller.abort();
      reject(passkeyRecoveryCancelled());
    };

    signal?.addEventListener('abort', onAbort, { once: true });
    // Cover an abort racing between the first check and listener registration.
    if (signal?.aborted) {
      onAbort();
      return;
    }

    void flight.promise.then(
      (token) => {
        if (!finish()) return;
        resolve(token);
      },
      (err: unknown) => {
        if (!finish()) return;
        reject(err);
      },
    );
  });
}

/** Join or start the one passkey recovery for the current session. */
async function recoverAccessWithPasskey(
  expected: PasskeySessionIdentity,
  signal?: AbortSignal,
): Promise<string> {
  const generationAtStart = sessionGeneration;
  try {
    if (signal?.aborted) throw passkeyRecoveryCancelled();
    let flight = inFlightPasskeyRecovery;
    if (
      flight &&
      (!identitiesEqual(flight.owner, expected) ||
        flight.owner.sessionId !== expected.sessionId)
    ) {
      throw new SessionChangedError();
    }
    if (flight?.controller.signal.aborted) {
      // The final previous waiter abandoned this recovery, so its controller can
      // never serve a new caller. Transfer teardown ownership before publishing a
      // successor; the old catch path rechecks this flag while holding the session
      // mutation lock and therefore cannot clear the successor's source session.
      const inheritedAuthLoss =
        flight.lifecycle.authLossConfirmed &&
        sessionOwnerIsCurrent(flight.sessionOwner);
      flight.lifecycle.ownsSessionTeardown = false;
      flight = startPasskeyRecovery(expected, inheritedAuthLoss);
    }
    flight ??= startPasskeyRecovery(expected);
    return await waitForPasskeyRecovery(flight, signal);
  } catch (err) {
    // A passkey-safe path can observe replacement inside refreshSingleFlight,
    // before startPasskeyRecovery reaches its ceremony-level teardown block.
    // Bind cleanup to the original generation and keep it local-only so the
    // peer session which won persistence remains untouched.
    if (isSessionChanged(err)) {
      await discardStaleLocalSession(generationAtStart);
    }
    throw err;
  }
}

/**
 * Sign out. Best-effort: the gateway blocklists the access token's jti, then the
 * local session is cleared regardless of the network outcome so the client is
 * always logged out locally. Dispatches the session-expired event so the boot
 * gate routes back to the login screen.
 *
 * @returns Resolves once the local session is cleared.
 */
export async function logout(): Promise<void> {
  const generationAtStart = sessionGeneration;
  if (!localSessionMayContinue(classifyLocalSessionOwnership())) {
    await discardStaleLocalSession(generationAtStart);
    return;
  }
  const ownerAtStart = captureSessionOwner();
  try {
    await request('/v1/auth/logout', { method: 'POST', body: {}, retry: false });
  } catch {
    // A failed logout call still clears the local session below; the access
    // token expires on its own even if the blocklist write did not land.
  }
  await endSessionLocally(ownerAtStart, { ownerMatch: 'lineage' });
}

/**
 * Run a refresh under the single-flight guard. Concurrent callers share the one
 * in-flight promise. Errors are rethrown without changing session state: the
 * ordinary caller ends a hard-rejected session, while an explicitly safe
 * pre-signing caller may replace it through passkey authentication.
 *
 * @returns The fresh access token, or null when none was returned.
 */
function refreshSingleFlight(expectedSessionId: string | null = null): Promise<string | null> {
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async (): Promise<string | null> => {
    let ownerAtStart: SessionOwnerSnapshot | null = null;

    try {
      return await withSessionMutationLock(async () => {
        // Re-read ownership only after acquiring the browser-wide lock. A peer
        // may have rotated the shared token while this tab was waiting.
        const generationAtStart = sessionGeneration;
        if (!isRealSession()) throw new SessionChangedError();
        if (!localSessionMayContinue(classifyLocalSessionOwnership())) {
          throw new SessionChangedError();
        }
        const refreshToken = getRefreshToken();
        const persistedOwnerAtStart = currentPersistedSessionOwner(refreshToken);
        const sessionIdAtStart = persistedOwnerAtStart.sessionId;
        ownerAtStart = {
          generation: generationAtStart,
          refreshToken,
          sessionId: sessionIdAtStart,
        };

        if (persistedOwnerAtStart.status !== 'valid') {
          throw new SessionChangedError();
        }
        if (!refreshToken) {
          throw new ApiError('no refresh token', { status: 401 });
        }
        if (
          expectedSessionId &&
          (sessionIdAtStart !== expectedSessionId ||
            readJwtSessionId(refreshToken) !== expectedSessionId)
        ) {
          throw new SessionChangedError();
        }

        // A refresh token is single-use. Hold the same cross-tab lock from this
        // read through gateway consumption and successor publication so another
        // tab can only read and spend the rotated successor.
        const pair = await request<TokenPairResponse>('/v1/auth/refresh', {
          method: 'POST',
          auth: false,
          retry: false,
          body: { refresh_token: refreshToken },
        });

        // Keep the defensive ownership check for session writes that bypass this
        // module or storage failures that can still change the observed owner.
        if (!refreshOwnerIsCurrent(generationAtStart, refreshToken, sessionIdAtStart)) {
          throw new SessionChangedError();
        }

        const persisted = storeRefreshedPair({
          accessToken: pair.access_token,
          refreshToken: pair.refresh_token,
          sessionId: pair.session_id?.trim() || sessionIdAtStart || undefined,
        });
        if (!persisted) {
          // T0 was consumed server-side but its successor could not be shared.
          // End this local session and publish invalidation before releasing the
          // lock so no peer can mistake persisted T0 for a usable credential.
          endSessionLocallyUnlocked();
          throw new ApiError(
            'The refreshed session could not be saved safely. Sign in again to continue.',
            { status: 0, code: 'SESSION_PERSISTENCE_FAILED' },
          );
        }
        pendingPasskeyIdentity = null;
        return pair.access_token ?? null;
      }, true);
    } catch (err) {
      const apiError = err instanceof ApiError ? err : new ApiError(String(err));
      const failedOwner = ownerAtStart ?? {
        generation: sessionGeneration,
        refreshToken: null,
        sessionId: null,
      };
      throw new SessionRefreshError(
        apiError,
        failedOwner.generation,
        failedOwner.refreshToken,
        failedOwner.sessionId,
      );
    }
  })();

  // Clear the guard once the refresh settles, regardless of outcome, so the next
  // refresh can start fresh. Handle both branches explicitly: a bare `finally`
  // would create a second rejected promise when refresh fails, even though the
  // original rejection is already handled by the caller.
  void inFlightRefresh.then(
    () => {
      inFlightRefresh = null;
    },
    () => {
      inFlightRefresh = null;
    },
  );
  return inFlightRefresh;
}

/**
 * Decide whether the current access token is missing or close enough to expiry
 * to refresh. The expiry is read from the JWT's exp claim without verifying the
 * signature, which is only a heuristic to schedule the refresh; the gateway
 * re-verifies the token on every call. A token whose exp cannot be read is
 * treated as needing a refresh so a malformed token does not get stuck.
 *
 * @param leewaySeconds Refresh when expiry is within this many seconds.
 * @returns True when a refresh should run.
 */
function shouldRefresh(leewaySeconds: number): boolean {
  const token = readAccessTokenForExpiry();
  if (!token) return true;
  const exp = readJwtExpSeconds(token);
  if (exp === null) return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return exp - nowSeconds <= leewaySeconds;
}

/**
 * Read the current access token solely to inspect its expiry. The session module
 * does not expose the access token by value, so this reaches through the api
 * provider hook, which returns whatever token the next request would send.
 *
 * @returns The current access token, or null.
 */
function readAccessTokenForExpiry(): string | null {
  // Read through the injected getter rather than a static import to avoid an
  // import cycle with the boot wiring; the provider hook is the same value the
  // api layer would attach. session.getToken returns the active access token for
  // a real session.
  return getActiveAccessToken();
}

/**
 * The active access token getter, wired by the boot module from the session
 * module. Kept as a hook rather than a static import so this module has no
 * import cycle with the boot wiring and tests can inject a token.
 */
let getActiveAccessToken: () => string | null = () => null;

/**
 * Install the active-access-token getter. The boot module wires this to the
 * session module's getToken so the expiry heuristic reads the live token.
 *
 * @param getter Returns the current access token.
 */
export function setActiveAccessTokenGetter(getter: () => string | null): void {
  getActiveAccessToken = getter;
}

/** Persist the pending login record for the IdP round trip. */
function writePending(pending: PendingLogin): void {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    }
  } catch {
    // sessionStorage being unavailable means the callback cannot complete; the
    // callback view surfaces the missing-pending error if so.
  }
}

/**
 * Read the pending login record set before the IdP redirect.
 *
 * @returns The pending record, or null when absent or unparseable.
 */
function readPending(): PendingLogin | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingLogin> | null;
    if (
      parsed &&
      typeof parsed.provider === 'string' &&
      typeof parsed.oauthSessionId === 'string' &&
      typeof parsed.expectedState === 'string' &&
      typeof parsed.expiresAtMillis === 'number'
    ) {
      return parsed as PendingLogin;
    }
  } catch {
    // A corrupt record is treated as no pending login.
  }
  return null;
}

/** Remove the pending login record. */
function clearPending(): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // Best-effort: a record that cannot be removed simply expires by its ttl.
  }
}

/**
 * Longest a watchdog tick will ever sleep, in milliseconds. A session that
 * expires further out than this is still re-checked once a minute rather than
 * slept through in one long timer, so a suspended laptop or a clock change
 * cannot leave the app sitting past an expiry it already passed.
 */
const WATCHDOG_MAX_SLEEP_MS = 60_000;

/** Refresh an access token this long before its signed expiry. */
const ACCESS_REFRESH_LEEWAY_MS = 60_000;

/** Re-check at least this often when no readable access-token expiry exists. */
const ACCESS_REFRESH_MAX_SLEEP_MS = 60_000;

/**
 * The stop function of the running watchdog, or null when none is running. Used
 * to make a second start a no-op: the timer lives in the closure, so two starts
 * would leave two sets of listeners fighting over one schedule, and stopping the
 * first would strand the second with listeners but no timer.
 */
let stopWatchdog: (() => void) | null = null;

/** Stop function for the single access-token refresh scheduler. */
let stopAccessRefreshScheduler: (() => void) | null = null;

/**
 * Tear down the local session and tell the UI to route to sign-in.
 *
 * The single place a session ends locally, whether the user signed out, a
 * refresh was rejected, or the watchdog found the refresh token expired. Before
 * this existed the three sites had drifted: only sign-out dropped the tracked
 * in-flight swaps, so an expired session left the previous account's swaps in the
 * store for whoever signed in next on the same browser.
 */
interface EndSessionLocallyOptions {
  /** Optional state predicate evaluated while the session mutation lock is held. */
  additionalGuard?: () => boolean;
  /** Refuse when the browser cannot serialize this mutation across tabs. */
  requireCrossTab?: boolean;
  /** Exact for stale async work; lineage lets explicit logout survive rotation. */
  ownerMatch?: 'exact' | 'lineage';
}

async function endSessionLocally(
  expectedOwner: SessionOwnerSnapshot = captureSessionOwner(),
  options: EndSessionLocallyOptions = {},
): Promise<boolean> {
  return withSessionMutationLock(() => {
    if (options.additionalGuard?.() === false) return false;
    if (discardStaleLocalSessionUnlocked(expectedOwner.generation)) {
      return true;
    }
    const ownerIsCurrent =
      options.ownerMatch === 'lineage'
        ? sessionLineageIsCurrent(expectedOwner)
        : sessionOwnerIsCurrent(expectedOwner);
    if (!ownerIsCurrent) return false;

    endSessionLocallyUnlocked();
    return true;
  }, options.requireCrossTab);
}

/**
 * Discard this tab's invalidated or replaced epoch without shared writes.
 *
 * A same-tab login increments the generation and defeats stale teardown. A peer
 * login does not change that generation, so the old local bearer is discarded
 * while the peer's refresh and metadata remain untouched.
 */
async function discardStaleLocalSession(
  expectedGeneration: number,
): Promise<boolean> {
  return withSessionMutationLock(() =>
    discardStaleLocalSessionUnlocked(expectedGeneration),
  );
}

/** Apply replacement/invalidation teardown while the mutation lock is held. */
function discardStaleLocalSessionUnlocked(
  expectedGeneration: number,
): boolean {
  if (expectedGeneration !== sessionGeneration || !isRealSession()) {
    return false;
  }

  // Re-read the stable-local versus shared owner relationship inside the lock.
  // A same-account passkey successor remains recoverable; every other peer
  // replacement is discarded locally without touching peer persistence.
  const ownership = classifyLocalSessionOwnership();
  if (localSessionMayContinue(ownership)) {
    return false;
  }

  // This tab still owns the exact legacy refresh credential; the problem is
  // that it has no issuer-signed sid and therefore cannot be refreshed safely.
  // End it authoritatively so reload cannot resume the same stranded session.
  if (ownership === 'legacy-sidless') {
    endSessionLocallyUnlocked();
    return true;
  }

  sessionGeneration += 1;
  authRequestGeneration += 1;
  pendingPasskeyIdentity = null;
  clearStaleSessionLocally();
  clearSwapIdentityToken();
  clearPendingSwaps();
  signalSessionExpired();
  return true;
}

/**
 * Preserve a passkey-authenticated account for explicit recovery after its
 * bearer credentials become unusable; all other providers end normally.
 */
async function suspendOrEndSessionAfterAuthLoss(
  expectedOwner: SessionOwnerSnapshot,
  additionalGuard: (() => boolean) | undefined = undefined,
): Promise<boolean> {
  return withSessionMutationLock(() => {
    if (!sessionOwnerIsCurrent(expectedOwner) || additionalGuard?.() === false) {
      return false;
    }

    if (!suspendPasskeySessionUnlocked()) endSessionLocallyUnlocked();
    return true;
  });
}

/** Suspend the current passkey session while the caller owns the mutation lock. */
function suspendPasskeySessionUnlocked(): boolean {
  if (getSessionMeta()?.provider !== 'passkey') return false;
  const identity = recoverablePasskeyIdentity();
  if (!identity) return false;
  pendingPasskeyIdentity = identity;
  requirePasskeyReauthentication();
  return true;
}

/** Apply session teardown while the caller owns the session mutation lock. */
function endSessionLocallyUnlocked(): void {
  // Bump first, so any refresh already awaiting the network sees a changed
  // generation the moment it resumes.
  sessionGeneration += 1;
  authRequestGeneration += 1;
  pendingPasskeyIdentity = null;
  clearSession();
  // Drop the cached swap-identity token so one minted for this session is not
  // reused afterwards; the next swap call mints a fresh one.
  clearSwapIdentityToken();
  // Drop any tracked in-flight swaps so the next account that signs in on this
  // browser never resumes polling or displaying a swap that was not theirs.
  clearPendingSwaps();
  signalSessionExpired();
}

/**
 * When the current session stops being refreshable, in milliseconds since epoch.
 *
 * Read from the persisted refresh token's exp claim without verifying the
 * signature — the same unverified-read heuristic the access-token path already
 * uses, and safe for the same reason: it only decides which screen to show, and
 * the gateway re-verifies every token it is actually sent.
 *
 * @returns The expiry in milliseconds, or null when there is no refresh token or
 *   its expiry cannot be read.
 */
export function sessionExpiresAtMillis(): number | null {
  if (isPasskeyReauthenticationRequired()) return null;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  const exp = readJwtExpSeconds(refreshToken);
  return exp === null ? null : exp * 1000;
}

/**
 * End the session if it is no longer refreshable.
 *
 * This is the piece that was missing entirely: nothing in the app watched the
 * clock, so an expired session was only ever discovered by an outbound request
 * failing. A tab left idle past expiry stayed on the dashboard indefinitely
 * because no request was made to fail. Checking the refresh token's own expiry
 * makes the session end on time whether or not anything is being fetched.
 *
 * Deliberately conservative in two places. It does nothing without a real OAuth
 * session, so the dev-token fallback is untouched. And a refresh token whose
 * expiry cannot be read is left alone rather than assumed dead, so an opaque or
 * malformed token degrades to the pre-existing behaviour (the 401 path ends it)
 * instead of logging a user out on a parse failure.
 *
 * @returns True when this call ended the session or suspended it for passkey
 *   reauthentication.
 */
export async function checkSessionExpiry(): Promise<boolean> {
  const ownerAtStart = captureSessionOwner();
  return withSessionMutationLock(() => {
    if (discardStaleLocalSessionUnlocked(ownerAtStart.generation)) {
      return true;
    }
    if (!sessionOwnerIsCurrent(ownerAtStart) || !isRealSession()) return false;
    if (isPasskeyReauthenticationRequired()) return true;

    // A refresh or recovery owns the expiring token and decides whether its
    // replacement succeeds. Expiry must not clear underneath that operation.
    if (inFlightRefresh || inFlightPasskeyRecovery) return false;

    const refreshToken = getRefreshToken();
    const refreshExp = refreshToken ? readJwtExpSeconds(refreshToken) : null;
    let expired = refreshToken !== null && refreshExp !== null
      ? Date.now() >= refreshExp * 1000
      : false;

    // Losing refresh persistence does not invalidate a still-live in-memory
    // access JWT. Let it authorize requests until its own signed expiry, then
    // suspend a passkey session (or end a social session) because no renewal
    // credential remains. A missing access token means the session is already
    // unusable; an unreadable access JWT stays gateway-decided as before.
    if (!refreshToken) {
      const accessToken = getToken();
      const accessExp = accessToken ? readJwtExpSeconds(accessToken) : null;
      expired = !accessToken || (accessExp !== null && Date.now() >= accessExp * 1000);
    }
    if (!expired) return false;

    if (!suspendPasskeySessionUnlocked()) endSessionLocallyUnlocked();
    return true;
  });
}

/**
 * Start watching for session expiry. Called once from the boot module.
 *
 * Two triggers, because neither alone is enough. The timer covers a tab that
 * stays open and visible across the expiry moment. The visibility and focus
 * listeners cover the case the timer cannot: browsers throttle timers hard in a
 * backgrounded tab, so a tab left in the background for hours may not get its
 * tick until the user returns — checking on return is what makes the redirect
 * feel immediate rather than up to a minute late.
 *
 * The timer re-arms itself after every tick rather than being scheduled once at
 * the expiry instant, so a token rotated by a refresh is picked up on the next
 * tick with no extra wiring, and a later sign-in on the same page load is
 * watched without restarting anything.
 *
 * @returns A function that stops the watchdog, for tests and hot reload.
 */
export async function startSessionWatchdog(): Promise<() => void> {
  if (typeof window === 'undefined') return () => {};

  // Already watching: hand back the running watchdog's stop rather than starting a
  // second one. Guards a Vite HMR update that re-executes the boot module, and any
  // future second caller.
  if (stopWatchdog) return stopWatchdog;

  // Closure-scoped, not module-scoped, so the timer belongs to exactly one
  // watchdog and cannot be cleared or overwritten by another.
  let timer: ReturnType<typeof setTimeout> | null = null;

  const checkAndSchedule = async (): Promise<void> => {
    await checkSessionExpiry();
    schedule();
  };

  const schedule = (): void => {
    if (timer !== null) clearTimeout(timer);
    const expiresAt = sessionExpiresAtMillis();
    const untilExpiry = expiresAt === null ? WATCHDOG_MAX_SLEEP_MS : expiresAt - Date.now();
    // Wake exactly at expiry when that is sooner than the ceiling, so the
    // redirect lands on time rather than up to a minute late. The floor keeps a
    // past-due or malformed expiry from spinning the timer at zero delay.
    const delay = Math.max(1_000, Math.min(untilExpiry, WATCHDOG_MAX_SLEEP_MS));
    timer = setTimeout(() => void checkAndSchedule(), delay);
  };

  const onWake = (): void => {
    if (document.visibilityState === 'hidden') return;
    void checkAndSchedule();
  };

  window.addEventListener('focus', onWake);
  document.addEventListener('visibilitychange', onWake);

  // Claim the singleton slot before the asynchronous first check so two callers
  // cannot both register listeners while one is waiting for the session lock.
  stopWatchdog = (): void => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    window.removeEventListener('focus', onWake);
    document.removeEventListener('visibilitychange', onWake);
    stopWatchdog = null;
  };

  // Check immediately: the common case is a tab restored, or the app reopened,
  // after the session already expired, and that must not wait for a first tick.
  await checkSessionExpiry();
  schedule();
  return stopWatchdog;
}

/**
 * Keep an open real session supplied with a live access token even when no view
 * is making authenticated requests.
 *
 * Request-driven refresh remains the final safety net, but it cannot run on an
 * idle Home screen. This scheduler reads the current access JWT expiry and wakes
 * one minute before it. A resumed session has no in-memory access token, so it
 * refreshes on the first scheduler tick. Focus and visibility recovery also run
 * the check immediately because background timers may have been throttled.
 *
 * @returns A function that stops the scheduler.
 */
export function startAccessTokenRefreshScheduler(): () => void {
  if (typeof window === 'undefined') return () => {};
  if (stopAccessRefreshScheduler) return stopAccessRefreshScheduler;

  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  const schedule = (): void => {
    if (stopped) return;
    if (timer !== null) clearTimeout(timer);

    const token = readAccessTokenForExpiry();
    const exp = token ? readJwtExpSeconds(token) : null;
    const untilRefresh =
      exp === null
        ? ACCESS_REFRESH_MAX_SLEEP_MS
        : exp * 1000 - Date.now() - ACCESS_REFRESH_LEEWAY_MS;
    const delay = Math.max(1_000, Math.min(untilRefresh, ACCESS_REFRESH_MAX_SLEEP_MS));
    timer = setTimeout(() => void refreshAndReschedule(), delay);
  };

  const refreshAndReschedule = async (): Promise<void> => {
    if (stopped) return;
    if (!(await checkSessionExpiry()) && isRealSession()) {
      await ensureFreshAccessToken().catch(() => {
        // A rejected refresh already ends the session; a transient failure is
        // retried by the next scheduler tick or authenticated request.
      });
    }
    schedule();
  };

  const onWake = (): void => {
    if (document.visibilityState === 'hidden') return;
    void refreshAndReschedule();
  };

  window.addEventListener('focus', onWake);
  document.addEventListener('visibilitychange', onWake);
  schedule();

  stopAccessRefreshScheduler = (): void => {
    stopped = true;
    if (timer !== null) clearTimeout(timer);
    timer = null;
    window.removeEventListener('focus', onWake);
    document.removeEventListener('visibilitychange', onWake);
    stopAccessRefreshScheduler = null;
  };
  return stopAccessRefreshScheduler;
}

/** Dispatch the session-expired event so the boot gate routes to login. */
function signalSessionExpired(): void {
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }
  } catch {
    // A failure to dispatch is non-fatal; the next gate check still reflects the
    // cleared session.
  }
}
